import type { EnvironmentId } from "@cleo/contracts";
import { ManagedRelay } from "@cleo/client-runtime/relay";
import * as Effect from "effect/Effect";
import { fetchPrimary } from "~/environments/primary/fetch";
import { runtime } from "~/lib/runtime";
import { readPreparedConnection } from "~/state/session";

/** Legacy Settings must show the same roots that session birth resolves.
 * This is a read of the addressed host's existing door, never a binding write. */
export async function readWorldFolderChoices(
  worldKey: string,
  environmentId: EnvironmentId,
  primaryEnvironmentId: EnvironmentId | null,
  signal: AbortSignal,
): Promise<{ codebase: string[]; files: string[] }> {
  const path = "/api/neucleos/world-source-roots";
  const init: RequestInit = {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ worldKey }),
    signal,
  };
  let response: Response;
  if (environmentId === primaryEnvironmentId) response = await fetchPrimary(path, init);
  else {
    const connection = readPreparedConnection(environmentId);
    if (!connection) throw new Error("Connect to this device to read its saved folders.");
    const url = new URL(path, connection.httpBaseUrl).toString();
    const headers = new Headers(init.headers);
    const auth = connection.httpAuthorization;
    if (auth?._tag === "Bearer") headers.set("authorization", `Bearer ${auth.token}`);
    if (auth?._tag === "Dpop") {
      const proof = await runtime.runPromise(
        Effect.gen(function* () {
          const signer = yield* ManagedRelay.ManagedRelayDpopSigner;
          return yield* signer.createProof({ method: "POST", url, accessToken: auth.accessToken });
        }),
      );
      headers.set("authorization", `DPoP ${auth.accessToken}`);
      headers.set("dpop", proof);
    }
    response = await fetch(url, { ...init, headers, credentials: auth ? "omit" : "include" });
  }
  const body: unknown = await response.json();
  if (!response.ok || !body || typeof body !== "object")
    throw new Error("The saved folders could not be read from this device.");
  const result = body as { ok?: boolean; roots?: unknown; unavailable?: unknown };
  if (
    result.ok !== true ||
    !Array.isArray(result.roots) ||
    !Array.isArray(result.unavailable) ||
    result.unavailable.length > 0
  )
    throw new Error("A saved folder is unavailable. Restore access before editing these lists.");
  const choices = { codebase: [] as string[], files: [] as string[] };
  for (const root of result.roots) {
    if (
      !root ||
      typeof root !== "object" ||
      typeof root.path !== "string" ||
      !["root-codebase", "world-files"].includes(root.group)
    )
      throw new Error("The device returned an incomplete folder list.");
    choices[root.group === "root-codebase" ? "codebase" : "files"].push(root.path);
  }
  return choices;
}
